import { useMemo } from 'react';
import { SwitchAccount } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { DataGridProps, GridColDef, type GridRowId } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { AdminUsersQuery, Role, UserPlan } from '../../../../api/gql';
import {
  renderCellOrDefault,
  renderCellSelect,
  renderNumberOrDefault,
} from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { useImpersonate } from '../../../../hooks/useImpersonate';

type Props = {
  currentUserId?: string;
  users?: AdminUsersQuery['adminUsers']['results'];
  onRoleChange: (userId: GridRowId, role: Role) => void;
  onPlanChange: (userId: GridRowId, plan: UserPlan) => void;
  isUpdating: boolean;
} & Partial<DataGridProps>;

export const AdminUsersTable = ({ currentUserId, users, onRoleChange, onPlanChange, isUpdating, ...rest }: Props) => {
  const { t } = useTranslation('page-admin-users');
  const { impersonateUser } = useImpersonate();

  const ROLE_OPTIONS = useMemo(
    () =>
      Object.values(Role)
        .reverse()
        .map((value) => ({
          value,
          label: value,
        })),
    [],
  );

  const PLAN_OPTIONS = useMemo(
    () =>
      Object.values(UserPlan)
        .reverse()
        .map((value) => ({
          value,
          label: value === UserPlan.Complete ? t('complete') : value,
        })),
    [t],
  );

  const rows = users ?? [];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lastName',
        headerName: t('table.lastName') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'firstName',
        headerName: t('table.firstName') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'email',
        headerName: t('table.email') as string,
        flex: 1,
        minWidth: 200,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'phoneNumber',
        headerName: t('table.phoneNumber') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vatId',
        headerName: t('table.vat') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'businessName',
        headerName: t('table.businessName') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'streetAddress',
        headerName: t('table.streetAddress') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'city',
        headerName: t('table.city') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'zipCode',
        headerName: t('table.zipCode') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'country',
        headerName: t('table.country') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'role',
        headerName: t('table.role') as string,
        flex: 1,
        minWidth: 120,
        renderCell: (params) =>
          renderCellSelect<Role>(params, ROLE_OPTIONS, onRoleChange, isUpdating || params.row.id === currentUserId),
      },
      {
        field: 'plan',
        headerName: t('table.plan') as string,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => renderCellSelect<UserPlan>(params, PLAN_OPTIONS, onPlanChange, isUpdating),
      },
      {
        field: 'remindersCount',
        headerName: t('table.remindersCount') as string,
        flex: 1,
        minWidth: 130,
        renderCell: renderNumberOrDefault,
      },
      {
        field: 'id',
        headerName: '',
        type: 'actions',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Button startIcon={<SwitchAccount />} onClick={() => impersonateUser(params.value)}>
            {t('impersonate')}
          </Button>
        ),
      },
    ],
    [impersonateUser, currentUserId, ROLE_OPTIONS, PLAN_OPTIONS, t, onRoleChange, onPlanChange, isUpdating],
  );

  return (
    <>
      <Table
        id={'admin-users'}
        {...rest}
        columns={columns}
        rows={rows}
        experimentalFeatures={{ columnGrouping: true }}
        columnGroupingModel={[
          {
            groupId: 'contactInfo',
            headerName: t('table.contactInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [{ field: 'firstName' }, { field: 'lastName' }, { field: 'email' }, { field: 'phoneNumber' }],
          },
          {
            groupId: 'billingInfo',
            headerName: t('table.billingInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [
              { field: 'vatId' },
              { field: 'businessName' },
              { field: 'streetAddress' },
              { field: 'city' },
              { field: 'zipCode' },
              { field: 'country' },
            ],
          },
          {
            groupId: 'systemInfo',
            headerName: t('table.systemInfo') as string,
            renderHeaderGroup: (params) => {
              return (
                <Typography variant={'body2'} color={'textSecondary'}>
                  {params.headerName}
                </Typography>
              );
            },
            children: [{ field: 'role' }, { field: 'plan' }, { field: 'remindersCount' }],
          },
        ]}
      />
    </>
  );
};
